<template>
  <v-container fluid>
    <v-row v-if="riskTypes && riskTypes.length">
      <v-col cols="12" class="px-0 pt-0">
        <RiskRatingPicker
          :params="params"
          :given-rating="selectedRating"
          :chips="true"
          :hide-cyber-subs="true"
          label="Map Overlay"
          placeholder="Risk Type"
          @change="handleChangeRating"
          @clear="handleClickClearRating"
        ></RiskRatingPicker>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="px-0 hyp-risk-level-container">
        <v-btn-toggle
          class="hyp-risk-level-btn-container px-4"
          v-model="selectedRiskLevels"
          dense
          group
          multiple
          @change="handleChangeSelectedRiskLevels"
        >
          <v-btn
            text
            v-for="(item, index) in riskLevels"
            :key="index"
            :value="item.value"
          >
            <v-avatar class="my-1" :color="item.bgColor" size="22"> </v-avatar>
          </v-btn>
        </v-btn-toggle>
        <div class="hyp-risk-level-text-container pr-1">
          <div class="text-c enter grey--text hyp-risk-level-text pull-left">
            Unstable
          </div>
          <div class="text-c enter grey--text hyp-risk-level-text pull-right">
            Stable
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashDebounce from "lodash/debounce";
import lodashFind from "lodash/find";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_RISK_RATINGS,
  MODULE_PREFIX_MAP_CONTROLS,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_DETAILS_SIDEBAR,
  GET_RISK_RATINGS_REFRESH_MAP,
} from "@/store/actions.type";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  getRiskRatings,
  getRiskRatingCategories,
  addToMap,
  removeFromMap,
  getMapTypeBySelectedRatings,
  populateDefaultPreferences,
} from "@/services/controlRisks/risks.service";
import {
  RISK_LEVELS,
  RISK_RATING_PRIMARIES,
} from "@/common/constants/controlRisks/risks";
import { getAllRegions, getCountryByCode } from "@/common/maps/utilities";
import { hasMaritimeAccess } from "@/services/user/user.service";
import { waitForMapReady } from "@/common/maps/maps";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { OVERLAYS_SECTION_RISK_RATINGS } from "@/common/constants/mapControls";

export default {
  name: "MapControlsOverlayRiskRatings",
  components: {
    RiskRatingPicker: () =>
      import("@/components/controlRisks/risks/RiskRatingPicker"),
  },
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    const initialSelectedRiskLevels = [1, 2, 3, 4, 5];
    return {
      hasMaritimeAccess: hasMaritimeAccess(),
      availableRiskTypes: [],
      availableSubRiskTypes: [],
      risks: null,
      riskTypes: null,
      selectedRating: null,
      selectedRiskLevels: initialSelectedRiskLevels,
      countries: [],
      countriesSeerist: [],
      citiesSeerist: [],
      zones: [],
      waters: [],
      ports: [],
    };
  },
  computed: {
    ...mapGetters({
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      preferences: MODULE_PREFIX_USER + "preferences",
      maps: MODULE_PREFIX_MAP + "maps",
      mainMapHijacker: MODULE_PREFIX_MAP + "mainMapHijacker",
      mapControlParams: MODULE_PREFIX_MAP_CONTROLS + "params",
      refreshMap: MODULE_PREFIX_RISK_RATINGS + "refreshMap",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    riskLevels() {
      return lodashOrderBy(RISK_LEVELS, ["value"], ["desc"]);
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.readPreferences();
      const { countries, zones, waters, ports } = await getRiskRatings();
      this.countries = countries;
      this.zones = zones;
      this.regions =
        (await getAllRegions())?.map((r) => ({
          value: r.properties.code,
          text: r.properties.name,
          countries: r.properties.countries.map((c) => ({
            value: c.isoAlpha2Code,
            text: c.name,
          })),
        })) || [];
      if (this.hasMaritimeAccess) {
        this.waters = waters;
        this.ports = ports;
      }
      await this.getFilteredRiskTypes();
      await this.loadRatings();
      this.setDefaultRiskType();
    },
    async getFilteredRiskTypes() {
      let riskRatingCategories = await getRiskRatingCategories();
      this.riskTypes = riskRatingCategories;
      this.riskTypes.forEach((r) => {
        if (r.parentId) {
          this.availableSubRiskTypes.push(r);
        } else {
          this.availableRiskTypes.push(r);
        }
      });
    },
    setDefaultRiskType() {
      if (!this.selectedRating) {
        this.selectedRating = this.riskTypes.find(
          (rt) => rt.id === RISK_RATING_PRIMARIES.security
        );
        let preferences = populateDefaultPreferences(this.preferences);
        preferences.controlRisks.riskRatings.ratings = this.selectedRating;
        this.$store.dispatch(
          MODULE_PREFIX_USER + SAVE_PREFERENCES,
          preferences
        );
      }
    },
    async handleChangeRating(rating) {
      if (rating) {
        this.selectedRating = rating;
        this.updateRatingSelectionInPreferences();
        await this.loadRatings();
      }
    },
    async handleClickClearRating() {
      this.selectedRating = null;
      this.updateRatingSelectionInPreferences();
      await this.loadRatings();
    },
    updateRatingSelectionInPreferences() {
      let preferences = populateDefaultPreferences(this.preferences);
      preferences.controlRisks.riskRatings.ratings = this.selectedRating;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_risk_ratings,
        EVENT_ACTIONS.clicked,
        `Type - ${this.selectedRating ? this.selectedRating.name : "None"}`
      );
    },
    async handleChangeSelectedRiskLevels() {
      this.updateSelectedRiskLevelsInPreferences();
      await this.loadRatings();
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_risk_ratings,
        EVENT_ACTIONS.clicked,
        `Levels - [${this.selectedRiskLevels}]`
      );
    },
    updateSelectedRiskLevelsInPreferences() {
      let preferences = populateDefaultPreferences(this.preferences);
      preferences.controlRisks.riskRatings.selectedRiskLevels = this.selectedRiskLevels;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    readPreferences() {
      let prefs = populateDefaultPreferences(this.preferences);
      if (typeof prefs.controlRisks?.riskRatings?.ratings === "object") {
        this.selectedRating = prefs.controlRisks.riskRatings.ratings;
      }
      if (prefs.controlRisks?.riskRatings?.selectedRiskLevels) {
        this.selectedRiskLevels =
          prefs.controlRisks.riskRatings.selectedRiskLevels;
      }
    },
    loadRatings: lodashDebounce(async function () {
      const riskTypeToMap = await getMapTypeBySelectedRatings(
        this.selectedRating
      );

      if (
        riskTypeToMap &&
        this.mapControlParams?.mapOverlaysControlEnabled &&
        this.mapControlParams?.mapOverlaysControlSelectedType ===
          OVERLAYS_SECTION_RISK_RATINGS &&
        !this.mainMapHijacker
      ) {
        const params = {
          map: this.map,
          mapClickEvent: this.mapClickEvent,
          riskScoresListToDisplay: this.selectedRiskLevels,
          riskType: riskTypeToMap,
          countries: this.countries,
          zones: this.zones,
        };

        if (this.hasMaritimeAccess) {
          const riskRatingCategories = await getRiskRatingCategories();
          const riskRatingMaritime = [];
          riskRatingCategories.forEach((rr) => {
            if (
              rr.id === RISK_RATING_PRIMARIES.maritime ||
              rr.parentId === RISK_RATING_PRIMARIES.maritime
            ) {
              riskRatingMaritime.push(rr.id);
            }
          });
          if (riskRatingMaritime.includes(riskTypeToMap)) {
            params.waters = this.waters;
            params.zones = null;
          }
        }
        this.attemptAddToMap(params);
      } else {
        removeFromMap({ map: this.map });
      }
    }, 500),
    async attemptAddToMap(params, attemptCount = 0) {
      if (!this.map && attemptCount < 50) {
        setTimeout(() => {
          this.attemptAddToMap(params, attemptCount + 1);
        }, 500);
      } else {
        await waitForMapReady(this.map);
        params.map = this.map;
        addToMap(params);
        this.$store.dispatch(
          MODULE_PREFIX_RISK_RATINGS + GET_RISK_RATINGS_REFRESH_MAP,
          false
        );
      }
    },
    async handleMapRefresh(force) {
      if (this.refreshMap || force) {
        if (
          this.mapControlParams?.mapOverlaysControlEnabled &&
          this.mapControlParams?.mapOverlaysControlSelectedType ===
            OVERLAYS_SECTION_RISK_RATINGS &&
          this.preferences?.controlRisks?.riskRatings?.ratings &&
          !this.mainMapHijacker
        ) {
          await this.loadRatings();
        } else {
          removeFromMap({ map: this.map });
        }
      }
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        const country = this.getRiskRatingsCountry(features[0]);
        if (country?.properties?.iso2Code) {
          features[0] = country;
          // grab the country feature from hyperion's country list
          const result = await getCountryByCode(country.properties.iso2Code);
          const feature = result ? JSON.parse(result) : null;
          if (feature && feature.data) {
            features[1] = feature.data;
          }
        }
        this.detailsSidebar.features = features;
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.monitorType =
          DETAIL_SIDEBAR_TYPES.controlRisksRatings;
        this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );
      }
    },
    getRiskRatingsCountry(feature) {
      let rrCountry;
      if (feature?.properties) {
        let countryISO2;
        if (feature.properties.CNTRY_ISO2) {
          countryISO2 = feature.properties.CNTRY_ISO2;
        } else if (feature.properties.CR_ID) {
          countryISO2 = feature.properties.CR_ID;
        } else if (feature.properties.code) {
          countryISO2 = feature.properties.code;
        }
        const zoneID = feature?.properties?.Zone_ID;
        if (this.countries && Array.isArray(this.countries)) {
          if (countryISO2) {
            const country = lodashFind(this.countries, [
              "properties.iso2Code",
              countryISO2.toUpperCase(),
            ]);
            if (country) {
              rrCountry = country;
            }
          }
          if (zoneID) {
            // if user clicks on a country's zone then get the zone_id and find the country
            const zone = lodashFind(this.zones, [
              "properties.code",
              zoneID.toUpperCase(),
            ]);
            if (zone?.properties?.parent?.code) {
              rrCountry = lodashFind(this.countries, [
                "properties.code",
                zone.properties.parent.code,
              ]);
            }
          }
        }
      }
      if (rrCountry) {
        return rrCountry;
      }
      return null;
    },
  },
  watch: {
    mainMapHijacker: {
      handler(newVal, oldVal) {
        this.handleMapRefresh(newVal !== oldVal);
      },
    },
    refreshMap: {
      handler() {
        this.handleMapRefresh();
      },
    },
    "mapControlParams.mapOverlaysControlEnabled": {
      handler() {
        this.handleMapRefresh("true");
      },
    },
    "mapControlParams.mapOverlaysControlSelectedType": {
      handler() {
        this.handleMapRefresh("true");
      },
    },
  },
};
</script>

<style scoped>
.hyp-risk-level-container {
  display: grid;
  place-items: center;
}
.hyp-risk-level-text-container {
  width: 80%;
}
.hyp-risk-level-btn-container .v-btn--active {
  border: 1px solid rgba(0, 174, 66, 0.41);
  box-shadow: 0 2px 11px 0 rgba(0, 174, 66, 0.23);
  border-radius: 9px;
  background-color: transparent !important;
  color: transparent !important;
}
.v-btn-toggle > .v-btn {
  width: 37px;
  height: 40px;
  margin: 0px 5px 0px 0px;
}
.v-btn-toggle > .v-btn.v-btn:first-child,
.v-btn-toggle > .v-btn.v-btn:last-child {
  border-radius: 9px;
}
.v-btn-toggle > .v-btn.v-btn:not(:first-child) {
  border-left-width: 1px;
}
.hyp-risk-level-text {
  font-size: 10px;
}
</style>
